import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Rocket, Lock, Globe, Check, Copy } from "lucide-react";
import { fetchPortalLauncher } from "../../redux/actionCreator";
import { renderNginxAuthSnippet } from "./nginxAuthSnippet";
import "./appsLauncher.scss";

/**
 * Portal launcher for "applications" — every URL the authenticated
 * user can reach, grouped by project + stage.
 *
 * The backend builds this list from project membership (super_admin
 * sees everything). For HARD restriction we pair this with the
 * forward-auth gate (`/v1/portal/auth-check`) — see the "Lock down
 * an app" modal for the nginx snippet operators paste on each app
 * server.
 */
const STAGES = ["development", "staging", "production"];
const STAGE_META = {
  development: { label: "DEV", color: "#2563eb", bg: "#dbeafe" },
  staging: { label: "STAGING", color: "#b45309", bg: "#fef3c7" },
  production: { label: "PROD", color: "#b91c1c", bg: "#fee2e2" },
};

export default function AppsLauncher() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("");
  const [snippetFor, setSnippetFor] = useState(null); // { url, projectName }

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const res = await dispatch(fetchPortalLauncher());
    setLoading(false);
    if (!res?.status) { setErr(res?.message || "Failed to load apps"); setProjects([]); return; }
    setProjects(res.data?.projects || []);
  }, [dispatch]);

  useEffect(() => { load(); }, [load]);

  const visibleProjects = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return projects;
    return projects
      .map((p) => {
        const stages = {};
        for (const s of STAGES) {
          stages[s] = (p.stages?.[s] || []).filter(
            (u) => `${p.project_name} ${u.label} ${u.url}`.toLowerCase().includes(q),
          );
        }
        const total = STAGES.reduce((n, s) => n + stages[s].length, 0);
        return total ? { ...p, stages, total } : null;
      })
      .filter(Boolean);
  }, [projects, filter]);

  return (
    <div className="apps-launcher">
      <div className="al-head">
        <div>
          <h4 className="m-0 d-inline-flex align-items-center gap-2">
            <Rocket size={20} /> My Apps
          </h4>
          <small className="text-muted">
            Applications you have access to. Click to open in a new tab.
          </small>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Form.Control
            size="sm"
            placeholder="Filter by name or URL…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ minWidth: 240 }}
          />
          <Button size="sm" variant="light" onClick={load} disabled={loading}>
            {loading ? <Spinner size="sm" animation="border" /> : "↻"}
          </Button>
        </div>
      </div>

      {err && <div className="alert alert-danger mt-3">{err}</div>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : visibleProjects.length === 0 ? (
        <div className="al-empty">
          <div className="empty-art">🪹</div>
          <p className="mb-1">
            {projects.length === 0
              ? "You don't have access to any apps yet."
              : `No apps match "${filter}".`}
          </p>
          {projects.length === 0 && (
            <small>Ask an admin to add you as a member of a project that has URLs configured.</small>
          )}
        </div>
      ) : (
        <div className="al-grid">
          {visibleProjects.map((p) => (
            <div key={p.project_id} className="al-project">
              <h5 className="al-project-name">{p.project_name}</h5>
              {STAGES.map((s) => {
                const list = p.stages?.[s] || [];
                if (!list.length) return null;
                const meta = STAGE_META[s];
                return (
                  <div key={s} className="al-stage">
                    <div className="al-stage-head">
                      <span
                        className="badge"
                        style={{ background: meta.bg, color: meta.color, fontWeight: 700 }}
                      >{meta.label}</span>
                      <span className="al-stage-count">{list.length}</span>
                    </div>
                    <div className="al-url-list">
                      {list.map((u) => (
                        <div key={u.id} className="al-url-row">
                          <a
                            href={u.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="al-url-link"
                            title={u.url}
                          >
                            <span className="al-url-label">
                              {u.label || "Open"}
                              {u.is_public && (
                                <span
                                  className="badge ms-2 d-inline-flex align-items-center gap-1"
                                  style={{ background: "#dcfce7", color: "#15803d", fontWeight: 600, fontSize: 9 }}
                                  title="Bypasses the portal gate — anyone with the link can access"
                                ><Globe size={9} /> PUBLIC</span>
                              )}
                            </span>
                            <span className="al-url-host">{safeHost(u.url)}</span>
                          </a>
                          <button
                            type="button"
                            className="al-url-snippet-btn"
                            title={u.is_public
                              ? "This URL is marked public — no gate needed"
                              : "Show nginx snippet to lock this URL to portal users"}
                            onClick={() => setSnippetFor({ url: u.url, projectName: p.project_name, stage: s, label: u.label, is_public: u.is_public })}
                          >
                            {u.is_public ? <Globe size={14} /> : <Lock size={14} />}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <NginxSnippetModal target={snippetFor} onHide={() => setSnippetFor(null)} />
    </div>
  );
}

function safeHost(u) {
  try { return new URL(u.startsWith("http") ? u : `https://${u}`).host; }
  catch { return u; }
}

function NginxSnippetModal({ target, onHide }) {
  const [copied, setCopied] = useState(false);
  const [portalBase, setPortalBase] = useState(() => {
    // Default to the same origin the portal is served from. The
    // operator can override — useful when the portal sits behind a
    // CDN or has a separate "auth subdomain".
    try { return window.location.origin; } catch { return "https://portal.example.com"; }
  });
  // Client-mode toggle. "browser" suits SPA + page apps; "api" suits
  // pure REST backends fronting native mobile clients; "hybrid"
  // handles both on the same hostname.
  const [mode, setMode] = useState("hybrid");
  const [apiPrefix, setApiPrefix] = useState("/api/");
  if (!target) return null;
  const snippet = renderNginxAuthSnippet({
    portalBase: portalBase.replace(/\/+$/, ""),
    appUrl: target.url,
    mode,
    apiPrefix,
  });
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  // Public URLs bypass the gate by design — show a short explanation
  // instead of the (irrelevant) nginx snippet so the operator doesn't
  // paste in a config that conflicts with their intent.
  if (target.is_public) {
    return (
      <Modal show={!!target} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-inline-flex align-items-center gap-2">
            <Globe size={18} /> <code>{target.label}</code> is public
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small mb-2">
            This URL has the <strong>Public</strong> flag set in the project editor.
            The portal forward-auth gate returns <code>200</code> for it without
            checking any token — meaning anyone with the link can reach it.
          </p>
          <div className="alert alert-info small mb-3">
            <strong>You don't need an nginx snippet for public URLs.</strong>{" "}
            Either leave nginx wide open, or paste the standard snippet if you
            want to layer the gate back on for this URL — it will still bypass
            because the portal checks the public flag before the JWT.
          </div>
          <p className="small text-muted mb-0">
            To remove the public status: open the project editor, find this URL,
            and toggle <strong className="d-inline-flex align-items-center gap-1"><Globe size={12} /> Public</strong> off.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal show={!!target} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <span className="d-inline-flex align-items-center gap-2">
            <Lock size={18} /> Lock <code>{target.label}</code> to portal users only
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Paste this into the nginx config for <code>{safeHost(target.url)}</code> on
          the app server. After reload, any request to this URL must carry a valid
          portal JWT — via cookie (browser) or <code>Authorization: Bearer</code>
          header (mobile / API client).
        </p>

        <div className="d-flex gap-3 align-items-center flex-wrap mb-2">
          <div className="d-flex gap-2 align-items-center">
            <Form.Label className="m-0 small">Client type:</Form.Label>
            <Form.Select
              size="sm"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              style={{ width: "auto" }}
            >
              <option value="hybrid">Hybrid (web + mobile)</option>
              <option value="browser">Browser only (SPA / pages)</option>
              <option value="api">API only (mobile / REST)</option>
            </Form.Select>
          </div>
          {mode === "hybrid" && (
            <div className="d-flex gap-2 align-items-center">
              <Form.Label className="m-0 small">API path prefix:</Form.Label>
              <Form.Control
                size="sm"
                value={apiPrefix}
                onChange={(e) => setApiPrefix(e.target.value)}
                style={{ maxWidth: 120 }}
                placeholder="/api/"
              />
            </div>
          )}
        </div>

        <div className="d-flex gap-2 align-items-center mb-2">
          <Form.Label className="m-0 small">Portal base URL:</Form.Label>
          <Form.Control
            size="sm"
            value={portalBase}
            onChange={(e) => setPortalBase(e.target.value)}
            style={{ maxWidth: 360 }}
          />
          <Button size="sm" variant={copied ? "success" : "primary"} onClick={copy}>
            <span className="d-inline-flex align-items-center gap-1">
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy snippet"}
            </span>
          </Button>
        </div>

        <pre className="al-snippet">{snippet}</pre>

        <div className="alert alert-info small mt-3 mb-2">
          <strong>Mobile apps:</strong> the app should call the portal's{" "}
          <code>POST /auth/validateManualLogin</code> at startup, store the returned
          JWT in secure storage (Keychain / EncryptedSharedPreferences), and send
          it as <code>Authorization: Bearer &lt;jwt&gt;</code> on every subsequent
          request to a gated URL. On <code>401</code> the app should treat the
          token as expired and re-authenticate. No cookies needed.
        </div>

        {(mode === "browser" || mode === "hybrid") && (
          <div className="alert alert-warning small mt-2">
            <strong>One-time setup for browser-flow cookies.</strong>{" "}
            The portal must mint <code>urltoken</code> with{" "}
            <code>Domain=.your-root-domain</code> (e.g. <code>.smarthatch.com</code>) and{" "}
            <code>SameSite=Lax</code> so it travels to your app subdomains. If the
            portal and apps live on completely different root domains you need a
            redirect-based ticket flow — flag and I'll build it.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm" variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
